import { useMemo, useTransition } from 'react'
import { useTranslation } from 'react-i18next'
import type { StatusId } from '@ludus/core'
import {
  usePipelineMetrics,
  usePipelineStages,
  usePipelineStageKinds,
  usePipelineManagers,
  usePipelineTotals,
  useStageLeads,
  useLeadStatsStatus,
} from '../data/usePipelinesSource.ts'
import { usePipelineId } from '../data/PipelineIdContext.tsx'
import { useStageState } from './useStageState.ts'
import { NoStages } from './NoStages.tsx'
import { PipelineHeader } from './Header/PipelineHeader.tsx'
import { StageDashboard } from './StageDashboard/StageDashboard.tsx'
import { FiltersBar } from './Filters/FiltersBar.tsx'
import { LeadsTable } from './LeadsTable/LeadsTable.tsx'
import { PaginationBar } from './LeadsTable/PaginationBar.tsx'
import { SidePanel } from './SidePanel/SidePanel.tsx'
import { Metric } from './components/Metric.tsx'
import { useSyncRun } from '../Sync/useSyncRun.ts'
import { SyncNoticeDialog } from '../Sync/SyncNoticeDialog.tsx'
import { useSyncNotice } from '../Sync/useSyncNotice.ts'
import { SyncBar } from '../Sync/SyncBar.tsx'

const eur = new Intl.NumberFormat('en-EU', {
  style: 'currency',
  currency: 'EUR',
  notation: 'compact',
  maximumFractionDigits: 1,
})

export function PipelineView() {
  const { t } = useTranslation()
  const now = useMemo(() => new Date(), [])
  const pipelineId = usePipelineId()
  const stages = usePipelineStages()
  const stageKinds = usePipelineStageKinds()
  const managers = usePipelineManagers()
  const metrics = usePipelineMetrics()
  const { run, active } = useSyncRun(pipelineId)
  const notice = useSyncNotice(run)
  const showWidget = active && !notice.open

  const selectedStageId = useStageState((s) => s.selectedStageId)
  const selectStage = useStageState((s) => s.selectStage)

  // Stage selection swaps the table + stats SWR keys. A transition keeps the
  // dashboard click feeling immediate while the new page/aggregates load
  // (SWR `keepPreviousData` holds the old rows in the meantime).
  const [, startStageTransition] = useTransition()
  const handleStageSelect = (id: StatusId) => {
    startStageTransition(() => selectStage(id))
  }

  const metricsByStage = useMemo(() => new Map(metrics.map((m) => [m.stageId, m])), [metrics])
  const stageIndex = stages.findIndex((s) => s.id === selectedStageId)
  const stage = stageIndex >= 0 ? stages[stageIndex] : null
  const stageMetrics = stage ? (metricsByStage.get(stage.id) ?? null) : null
  const prevStageName = stageIndex > 0 ? stages[stageIndex - 1].name : null
  const nextStageName =
    stageIndex >= 0 && stageIndex < stages.length - 1 ? stages[stageIndex + 1].name : null

  const { leads, total, isLoading: leadsLoading, error: leadsError } = useStageLeads()
  const { isLoading: statsLoading, error: statsError } = useLeadStatsStatus()

  // A failed read must be visually distinct from "no leads". Either the table
  // page or the stats aggregate erroring surfaces the table's error state; a
  // first-load (no rows yet) surfaces a loading state. `keepPreviousData` keeps
  // prior rows on screen while a new key loads, so loading only shows when the
  // table is genuinely empty.
  const tableError = leadsError ?? statsError
  const tableLoading = (leadsLoading || statsLoading) && leads.length === 0

  // Filtered pipeline-wide totals (ignore the selected stage) for the header.
  const { totalLeads, totalValue } = usePipelineTotals()
  // Naive end-to-end conversion: fraction of last-stage to first-stage counts.
  const e2eRate =
    metrics.length >= 2 && metrics[0].leadCount > 0
      ? (metrics[metrics.length - 1].leadCount / metrics[0].leadCount) * 100
      : 0

  if (stages.length === 0) {
    return (
      <div className="@container/pip flex h-full w-full flex-1 flex-col gap-3 overflow-hidden">
        <PipelineHeader
          stageCount={stages.length}
          totalLeads={totalLeads}
          totalValue={totalValue}
          e2eRate={e2eRate}
          defaultStageId={null}
        />
        {showWidget && run && <SyncBar run={run} />}
        <NoStages pipelineId={pipelineId} />
        <SyncNoticeDialog run={run} open={notice.open} onDismiss={notice.dismiss} />
      </div>
    )
  }

  return (
    <div className="@container/pip flex h-full w-full flex-1 flex-col gap-3 overflow-hidden">
      <PipelineHeader
        stageCount={stages.length}
        totalLeads={totalLeads}
        totalValue={totalValue}
        e2eRate={e2eRate}
        defaultStageId={selectedStageId}
      />

      {showWidget && run && <SyncBar run={run} />}

      <StageDashboard
        stages={stages}
        stageKinds={stageKinds}
        metricsByStage={metricsByStage}
        selectedStageId={selectedStageId}
        onSelect={handleStageSelect}
      />

      <div className="flex min-h-0 flex-1 gap-3 overflow-hidden">
        <main className="bg-gradient-panel-elevated flex flex-1 flex-col overflow-hidden rounded-xl border border-border shadow-[0_24px_48px_-32px_rgba(0,0,0,0.7)]">
          <header className="flex flex-wrap items-end justify-between gap-x-6 gap-y-3 border-b border-border/30 px-4 py-3">
            <div className="flex min-w-0 flex-wrap items-end gap-x-5 gap-y-2">
              {stage && stageMetrics ? (
                <>
                  <div className="flex min-w-0 flex-col gap-1">
                    <h2 className="truncate text-lg font-bold tracking-tight text-foreground sm:text-xl">
                      {stage.name}
                    </h2>
                    <span className="text-[10px] font-semibold uppercase tracking-[0.16em] text-muted">
                      {t('pages.pipelines.stage.deals', {
                        defaultValue: '{{count}} deals',
                        count: stageMetrics.leadCount,
                      })}
                    </span>
                  </div>
                  <Metric
                    value={eur.format(stageMetrics.totalValue)}
                    label={t('pages.pipelines.stage.valueLabel', { defaultValue: 'In stage' })}
                  />
                  <Metric
                    value={`${stageMetrics.medianAgeDays}d`}
                    label={t('pages.pipelines.stage.medianAgeLabel', {
                      defaultValue: 'Median age',
                    })}
                  />
                  {stageMetrics.stuckCount > 0 && (
                    <Metric
                      value={String(stageMetrics.stuckCount)}
                      label={t('pages.pipelines.stage.stuckLabel', { defaultValue: 'Stuck' })}
                      tone="warning"
                    />
                  )}
                </>
              ) : (
                <>
                  <div className="flex min-w-0 flex-col gap-1">
                    <h2 className="truncate text-lg font-bold tracking-tight text-foreground sm:text-xl">
                      {t('pages.pipelines.allLeads.title', { defaultValue: 'All leads' })}
                    </h2>
                    <span className="text-[10px] font-semibold uppercase tracking-[0.16em] text-muted">
                      {t('pages.pipelines.allLeads.count', {
                        defaultValue: '{{count}} leads',
                        count: totalLeads,
                      })}
                    </span>
                  </div>
                  <Metric
                    value={eur.format(totalValue)}
                    label={t('pages.pipelines.allLeads.valueLabel', {
                      defaultValue: 'Total value',
                    })}
                  />
                </>
              )}
            </div>
            <FiltersBar />
          </header>
          <div className="min-h-0 flex-1 overflow-y-auto">
            <LeadsTable
              leads={leads}
              total={total}
              managers={managers}
              now={now}
              isLoading={tableLoading}
              error={tableError}
            />
          </div>
          {/* Pinned to the bottom of the panel box (sibling of the scroll area,
              not inside it) — only while rows are actually shown. */}
          {!tableLoading && !tableError && total > 0 && <PaginationBar total={total} />}
        </main>
        <SidePanel
          metrics={stageMetrics}
          prevStageName={prevStageName}
          nextStageName={nextStageName}
          totalLeadsInStage={selectedStageId ? (stageMetrics?.leadCount ?? 0) : totalLeads}
        />
      </div>

      <SyncNoticeDialog run={run} open={notice.open} onDismiss={notice.dismiss} />
    </div>
  )
}
